import { List } from "immutable";
import _ from "lodash";

import { immutableRecords } from "../../types";
import findKeyPathOfNode from "./findKeyPathOfNode";
import findParentAndIndexOfNode from "./findParentAndIndexOfNode";
import findPositionOfNode from "./findPositionOfNode";
import positionToKeyPath from "./positionToKeyPath";

const positionWithinParent = (parentPosition, testPosition) => {
  const p = testPosition.slice(0, -1);
  if (_.isEqual(parentPosition, p)) {
    return testPosition[testPosition.length - 1];
  }
  return null;
};

const createNode = itemId => {
  return immutableRecords.TreeRecord({ id: itemId, children: List() });
};

const insertRecord = (tree, treeRecord, position) => {
  const fullKeyPath = positionToKeyPath(position);
  const finalIndex = _.last(fullKeyPath);
  const parentPath = fullKeyPath.slice(0, -1);
  return tree.updateIn(parentPath, children => children.insert(finalIndex, treeRecord));
};

export default (tree, itemIds, parent, index) => {
  const sortedItems = itemIds
    .map(o => {
      return { itemId: o, origPosition: findPositionOfNode(tree, o) };
    })
    .sort((a, b) => {
      const n = Math.min(a.origPosition.length, b.origPosition.length);
      for (let i = 0; i < n; i++) {
        if (a.origPosition[i] != b.origPosition[i]) {
          return a.origPosition[i] - b.origPosition[i];
        }
      }
      return 0;
    });

  // Filter out the children of any moving nodes
  // to avoid redundant processing
  // See ATR-908
  const filteredItems = sortedItems.filter((item, index) => {
    const itemsBefore = sortedItems.slice(0, index);
    return (
      itemsBefore.find(
        beforeItem =>
          beforeItem.origPosition.length < item.origPosition.length &&
          findParentAndIndexOfNode(tree, item.itemId, beforeItem.itemId) !== null
      ) === undefined
    );
  });

  const result = tree.withMutations(t => {
    // Iterate through the items, grab their tree, and then remove them
    const parentPosition = findPositionOfNode(t, parent);
    let nodesBeforeIndex = 0;
    filteredItems.forEach(obj => {
      if (obj.origPosition.length > 0) {
        // If the item is already part of the tree we are recording,
        // and occurs before our target index, we need to adjust our target index
        const posInside = positionWithinParent(parentPosition, obj.origPosition);
        if (!_.isNil(posInside) && posInside <= index) {
          nodesBeforeIndex++;
        }
        const keypath = findKeyPathOfNode(t, obj.itemId);
        obj.treeRecord = t.getIn(keypath);
        t.deleteIn(keypath);
      } else {
        obj.treeRecord = createNode(obj.itemId);
      }
    });
    let newPosition = findPositionOfNode(t, parent);
    newPosition.push(index - nodesBeforeIndex + 1);

    filteredItems.forEach(obj => {
      insertRecord(t, obj.treeRecord, newPosition.slice(0));
      newPosition[newPosition.length - 1]++;
    });
  });

  return result;
};
